import api from './api';
import {
  HelpCategory,
  HelpArticle,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreateArticleRequest,
  UpdateArticleRequest,
  PaginatedResponse,
} from '../types/helpCenter';

// Public APIs (no authentication)
export const helpCenterPublicApi = {
  // Get all categories
  getCategories: async (): Promise<HelpCategory[]> => {
    const response = await api.get('/help/categories');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get category by ID
  getCategory: async (id: number): Promise<HelpCategory> => {
    const response = await api.get(`/help/categories/${id}`);
    return response.data;
  },

  // Get articles by category
  getArticlesByCategory: async (
    categoryId: number,
    page = 0,
    size = 20,
    featured?: boolean
  ): Promise<PaginatedResponse<HelpArticle>> => {
    const response = await api.get(`/help/categories/${categoryId}/articles`, {
      params: { page, size, featured },
    });
    const data = response.data;
    if (data.content) {
      return data;
    }
    return {
      content: Array.isArray(data) ? data : [],
      totalElements: Array.isArray(data) ? data.length : 0,
      totalPages: 1,
      currentPage: page,
      size,
    };
  },

  // Get single article
  getArticle: async (id: number): Promise<HelpArticle> => {
    const response = await api.get(`/help/articles/${id}`);
    return response.data;
  },

  // Search articles
  searchArticles: async (
    query: string,
    categoryId?: number,
    page = 0,
    size = 20
  ): Promise<PaginatedResponse<HelpArticle>> => {
    const response = await api.get('/help/articles/search', {
      params: { q: query, categoryId, page, size },
    });
    const data = response.data;
    if (data.content) {
      return data;
    }
    return {
      content: Array.isArray(data) ? data : [],
      totalElements: Array.isArray(data) ? data.length : 0,
      totalPages: 1,
      currentPage: page,
      size,
    };
  },

  // Get featured articles
  getFeaturedArticles: async (): Promise<HelpArticle[]> => {
    const response = await api.get('/help/articles/featured');
    return Array.isArray(response.data) ? response.data : [];
  },
};

// Admin APIs (authentication required)
export const helpCenterAdminApi = {
  // Category Management
  createCategory: async (data: CreateCategoryRequest): Promise<HelpCategory> => {
    const response = await api.post('/help/admin/categories', data);
    return response.data;
  },

  updateCategory: async (id: number, data: UpdateCategoryRequest): Promise<HelpCategory> => {
    const response = await api.put(`/help/admin/categories/${id}`, data);
    return response.data;
  },

  deleteCategory: async (id: number): Promise<void> => {
    await api.delete(`/help/admin/categories/${id}`);
  },

  getAllCategories: async (): Promise<HelpCategory[]> => {
    const response = await api.get('/help/admin/categories');
    return Array.isArray(response.data) ? response.data : [];
  },

  // Article Management
  createArticle: async (data: CreateArticleRequest, images?: File[]): Promise<HelpArticle> => {
    // Validate required fields
    if (!data.title || !data.content || !data.categoryId) {
      throw new Error('Title, content, and categoryId are required');
    }
    
    if (images && images.length > 0) {
      // Multipart form data (Option B: Direct form fields)
      const formData = new FormData();
      formData.append('title', data.title);
      if (data.titleAr) formData.append('titleAr', data.titleAr);
      formData.append('content', data.content);
      if (data.summary) formData.append('summary', data.summary);
      formData.append('categoryId', data.categoryId.toString());
      if (data.tags && data.tags.length > 0) {
        // Support both array and comma-separated string
        if (Array.isArray(data.tags)) {
          formData.append('tags', data.tags.join(','));
        } else {
          formData.append('tags', data.tags);
        }
      }
      if (data.isPublished !== undefined) formData.append('isPublished', data.isPublished.toString());
      if (data.isFeatured !== undefined) formData.append('isFeatured', data.isFeatured.toString());
      if (data.order !== undefined) formData.append('order', data.order.toString());
      if (data.relatedArticleIds && data.relatedArticleIds.length > 0) {
        data.relatedArticleIds.forEach(id => formData.append('relatedArticleIds', id.toString()));
      }
      // Append images - backend expects 'images' field (can be multiple)
      images.forEach(image => formData.append('images', image));

      const response = await api.post('/help/admin/articles', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } else {
      // JSON (Option A: JSON only, no images)
      // Ensure categoryId is a number, not 0 or null
      const categoryId = Number(data.categoryId);
      if (!categoryId || isNaN(categoryId) || categoryId === 0) {
        throw new Error('categoryId must be a valid number greater than 0');
      }
      
      // Build request object explicitly to avoid any serialization issues
      const requestData: any = {
        title: data.title,
        titleAr: data.titleAr || '',
        content: data.content,
        categoryId: categoryId, // Explicitly set as number
      };
      
      // Add optional fields only if they exist
      if (data.summary) {
        requestData.summary = data.summary;
      }
      if (data.tags && data.tags.length > 0) {
        requestData.tags = data.tags;
      }
      if (data.isPublished !== undefined) {
        requestData.isPublished = data.isPublished;
      }
      if (data.isFeatured !== undefined) {
        requestData.isFeatured = data.isFeatured;
      }
      if (data.order !== undefined) {
        requestData.order = data.order;
      }
      if (data.relatedArticleIds && data.relatedArticleIds.length > 0) {
        requestData.relatedArticleIds = data.relatedArticleIds;
      }
      
      console.log('Creating article with data:', JSON.stringify(requestData, null, 2));
      console.log('CategoryId in request:', requestData.categoryId, 'Type:', typeof requestData.categoryId);
      
      const response = await api.post('/help/admin/articles', requestData);
      return response.data;
    }
  },

  updateArticle: async (id: number, data: UpdateArticleRequest, images?: File[]): Promise<HelpArticle> => {
    if (images && images.length > 0) {
      // Multipart form data for update with images
      const formData = new FormData();
      if (data.title) formData.append('title', data.title);
      if (data.titleAr) formData.append('titleAr', data.titleAr);
      if (data.content) formData.append('content', data.content);
      if (data.summary) formData.append('summary', data.summary);
      if (data.categoryId) formData.append('categoryId', data.categoryId.toString());
      if (data.tags) {
        if (Array.isArray(data.tags)) {
          formData.append('tags', data.tags.join(','));
        } else {
          formData.append('tags', data.tags);
        }
      }
      if (data.isPublished !== undefined) formData.append('isPublished', data.isPublished.toString());
      if (data.isFeatured !== undefined) formData.append('isFeatured', data.isFeatured.toString());
      if (data.order !== undefined) formData.append('order', data.order.toString());
      if (data.relatedArticleIds && data.relatedArticleIds.length > 0) {
        data.relatedArticleIds.forEach(id => formData.append('relatedArticleIds', id.toString()));
      }
      images.forEach(image => formData.append('images', image));

      const response = await api.put(`/help/admin/articles/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } else {
      // JSON update
      const response = await api.put(`/help/admin/articles/${id}`, data);
      return response.data;
    }
  },

  deleteArticle: async (id: number): Promise<void> => {
    await api.delete(`/help/admin/articles/${id}`);
  },

  getAllArticles: async (
    page = 0,
    size = 20,
    categoryId?: number,
    isPublished?: boolean,
    search?: string
  ): Promise<PaginatedResponse<HelpArticle>> => {
    const response = await api.get('/help/admin/articles', {
      params: { page, size, categoryId, isPublished, search },
    });
    const data = response.data;
    if (data.content) {
      return data;
    }
    return {
      content: Array.isArray(data) ? data : [],
      totalElements: Array.isArray(data) ? data.length : 0,
      totalPages: 1,
      currentPage: page,
      size,
    };
  },

  uploadArticleImages: async (id: number, images: File[]): Promise<HelpArticle> => {
    const formData = new FormData();
    // Backend expects 'images' field (can be multiple files)
    images.forEach(image => formData.append('images', image));

    const response = await api.post(`/help/admin/articles/${id}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

